import React, { useMemo, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';

import { Chip, Field, Panel, Segments, Empty } from '../kit.js';
import { ACCESS_GROUPS, useBuilding } from '../../state/building.js';
import { Icon } from './glyphs.js';
import { CODE_TONE, CREDENTIAL_TONE, codeStatus, credentialLabel, holderOf, newFob, spaced } from './model.js';

const EASE = [0.16, 1, 0.3, 1];

const TYPES = ['Resident', 'Staff', 'Vendor'];
const MEDIA = ['Fob', 'Mobile key', 'Fob and mobile key'];

/* Which groups a new credential starts with, by who it is for. The desk
   can change them before issuing; this only saves the common case. */
const PRESET = {
    Resident: ['Lobby', 'Garage', 'Gym', 'Pool', 'Rooftop', 'Package room'],
    Staff: ACCESS_GROUPS,
    Vendor: ['Lobby', 'Service']
};

const FILTERS = {
    All: () => true,
    Residents: c => c.type === 'Resident',
    Staff: c => c.type === 'Staff',
    Vendors: c => c.type === 'Vendor',
    Attention: c => c.status !== 'Active'
};

const BLANK = { name: '', unit: '', type: 'Resident', medium: 'Fob', number: '', groups: PRESET.Resident };

const initials = name => name.split(/\s+/).filter(Boolean).slice(0, 2).map(w => w[0]).join('').toUpperCase();

/* Credentials: every fob and mobile key in the building, who holds it,
   which doors it opens, and the controls the desk needs when one is
   lost or a contractor's job ends. Visitor codes residents create sit
   underneath, since the desk is who a guest calls when one fails. */
export function Credentials({ notify, index = 0 }) {
    const { credentials, guestCodes, add, patch, logAccess } = useBuilding();
    const reduced = useReducedMotion();
    const [query, setQuery] = useState('');
    const [filter, setFilter] = useState('All');
    const [form, setForm] = useState(BLANK);
    const [all, setAll] = useState(false);

    const shown = useMemo(() => {
        const q = query.trim().toLowerCase();
        return credentials.filter(FILTERS[filter]).filter(c => !q
            || c.name.toLowerCase().includes(q)
            || c.unit.toLowerCase().includes(q)
            || String(c.number).toLowerCase().includes(q));
    }, [credentials, filter, query]);

    const PAGE = 7;
    const visible = all || query ? shown : shown.slice(0, PAGE);
    const fobs = credentials.map(c => c.number);
    const codes = guestCodes.filter(c => codeStatus(c) === 'Active');

    const set = key => event => setForm(f => ({ ...f, [key]: event.target.value }));
    const setType = event => {
        const type = event.target.value;
        setForm(f => ({ ...f, type, groups: PRESET[type] }));
    };
    const toggleGroup = group => setForm(f => ({
        ...f,
        groups: f.groups.includes(group) ? f.groups.filter(g => g !== group) : ACCESS_GROUPS.filter(g => g === group || f.groups.includes(g))
    }));

    const issue = event => {
        event.preventDefault();
        if (!form.name.trim()) return notify('Add the credential holder\'s name first.');
        if (form.groups.length === 0) return notify('Choose at least one access group.');

        const prefix = form.type === 'Resident' ? '4' : form.type === 'Staff' ? '1' : '7';
        const number = /^\d{4,6}$/.test(form.number.trim()) && !fobs.includes(form.number.trim()) ? form.number.trim() : newFob(fobs, prefix);
        const base = { name: form.name.trim(), unit: form.unit.trim() || (form.type === 'Resident' ? 'TBD' : form.type), type: form.type, groups: form.groups, status: 'Active' };

        if (form.medium !== 'Fob') add('credentials', { ...base, medium: 'Mobile key', number: 'Invite sent' });
        if (form.medium !== 'Mobile key') add('credentials', { ...base, medium: 'Fob', number });

        logAccess({
            kind: 'system', tone: 'info', label: 'Credential', title: 'Credential issued', who: base.name,
            via: form.medium === 'Mobile key' ? 'Mobile key' : `Fob ${number}`, note: form.groups.join(', ')
        });
        notify(form.medium === 'Mobile key'
            ? `Mobile key invitation sent to ${base.name}.`
            : `Fob ${number} issued to ${base.name}: ${form.groups.join(', ')}.`);
        setForm(BLANK);
    };

    const setStatus = (c, status, title) => {
        patch('credentials', c.id, { status });
        logAccess({ kind: 'system', tone: status === 'Active' ? 'ok' : 'warn', label: 'Credential', title, who: c.name, via: credentialLabel(c) });
        notify(status === 'Active'
            ? `${c.name}'s ${c.medium.toLowerCase()} works again.`
            : `${c.name}'s ${c.medium.toLowerCase()} is suspended and opens no doors.`);
    };

    const replace = c => {
        const number = newFob(fobs, c.number[0] || '4');
        patch('credentials', c.id, { number, status: 'Active' });
        logAccess({ kind: 'system', tone: 'info', label: 'Credential', title: 'Fob replaced', who: c.name, via: `Fob ${c.number} to ${number}` });
        notify(`New fob ${number} issued to ${c.name}. Fob ${c.number} no longer opens any door.`);
    };

    const revoke = code => {
        patch('guestCodes', code.id, { status: 'Revoked' });
        logAccess({ kind: 'system', tone: 'warn', label: 'Visitor', title: 'Visitor code revoked', who: code.guest, via: `Code ${code.code}`, note: `Guest of ${code.unit}` });
        notify(`Code ${spaced(code.code)} for ${code.guest} no longer opens any door.`);
    };

    return (
        <div className="split acc-creds">
            <Panel as="form" index={index} className="panel--accent" onSubmit={issue}>
                <h4>Issue a credential</h4>
                <div className="form">
                    <Field label="Name" wide>
                        <input className="form-control" type="text" value={form.name} onChange={set('name')} placeholder="e.g. Jordan Blake" />
                    </Field>
                    <Field label={form.type === 'Resident' ? 'Unit' : 'Role or company'}>
                        <input className="form-control" type="text" value={form.unit} onChange={set('unit')} placeholder={form.type === 'Resident' ? 'e.g. 1204' : 'e.g. Apex Plumbing'} />
                    </Field>
                    <Field label="Holder">
                        <select className="form-control" value={form.type} onChange={setType}>
                            {TYPES.map(t => <option key={t}>{t}</option>)}
                        </select>
                    </Field>
                    <Field label="Credential">
                        <select className="form-control" value={form.medium} onChange={set('medium')}>
                            {MEDIA.map(m => <option key={m}>{m}</option>)}
                        </select>
                    </Field>
                    <Field label="Fob number">
                        <input
                            className="form-control"
                            type="tel"
                            inputMode="numeric"
                            pattern="[0-9]*"
                            maxLength={6}
                            value={form.number}
                            onChange={event => setForm(f => ({ ...f, number: event.target.value.replace(/\D/g, '') }))}
                            placeholder="Auto-assign"
                            disabled={form.medium === 'Mobile key'}
                        />
                    </Field>
                    <div className="field field--wide">
                        <span className="form-label">Access groups</span>
                        <div className="acc-pills" role="group" aria-label="Access groups">
                            {ACCESS_GROUPS.map(group => (
                                <button
                                    type="button"
                                    key={group}
                                    className={`acc-pill${form.groups.includes(group) ? ' acc-pill--on' : ''}`}
                                    aria-pressed={form.groups.includes(group)}
                                    onClick={() => toggleGroup(group)}
                                >
                                    <span className="acc-pill__box" aria-hidden="true">{form.groups.includes(group) ? <Icon name="check" size={12} /> : null}</span>
                                    {group}
                                </button>
                            ))}
                        </div>
                    </div>
                    <button type="submit" className="btn btn-primary"><Icon name="key" size={16} /> Issue credential</button>
                </div>
            </Panel>

            <div className="stack">
                <div className="acc-toolbar">
                    <h4>Credentials <span className="acc-faint">{credentials.filter(c => c.status === 'Active').length} active</span></h4>
                    <label className="acc-search">
                        <Icon name="search" size={16} />
                        <input type="search" value={query} onChange={e => setQuery(e.target.value)} placeholder="Search name, unit or fob" aria-label="Search credentials" />
                    </label>
                </div>
                <Segments
                    label="Filter credentials"
                    value={filter}
                    onChange={setFilter}
                    options={Object.keys(FILTERS).map(key => ({
                        value: key,
                        label: key === 'Attention' ? `Needs attention ${credentials.filter(FILTERS.Attention).length}` : key
                    }))}
                />

                {shown.length === 0 ? <Empty>No credentials match "{query}".</Empty> : (
                    <ul className="rows acc-cred-list">
                        <AnimatePresence initial={false}>
                            {visible.map(c => (
                                <motion.li
                                    key={c.id}
                                    layout={reduced ? false : 'position'}
                                    className="row acc-cred"
                                    data-type={c.type}
                                    initial={reduced ? false : { opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    exit={{ opacity: 0 }}
                                    transition={{ duration: 0.35, ease: EASE }}
                                >
                                    <span className="acc-avatar" aria-hidden="true">{initials(c.name)}</span>
                                    <div className="row__main">
                                        <span className="row__title">{c.name}</span>
                                        <span className="row__meta">
                                            {holderOf(c)} · <span className="acc-num">{credentialLabel(c)}</span>
                                        </span>
                                        <span className="acc-tags">
                                            {c.groups.slice(0, 4).map(g => <span className="acc-tag" key={g}>{g}</span>)}
                                            {c.groups.length > 4 && <span className="acc-tag acc-tag--more">+{c.groups.length - 4}</span>}
                                        </span>
                                    </div>
                                    <div className="row__end">
                                        <Chip tone={CREDENTIAL_TONE[c.status]}>{c.status}</Chip>
                                        {c.medium === 'Fob' && (c.status === 'Lost' || c.status === 'Active') && (
                                            <button type="button" className={`btn btn-sm ${c.status === 'Lost' ? 'btn-primary' : 'btn-outline-light'}`} onClick={() => replace(c)}>
                                                Replace fob
                                            </button>
                                        )}
                                        {c.status === 'Active' ? (
                                            <button type="button" className="btn btn-outline-light btn-sm" onClick={() => setStatus(c, 'Suspended', 'Credential suspended')}>Suspend</button>
                                        ) : (
                                            <button type="button" className="btn btn-outline-light btn-sm" onClick={() => setStatus(c, 'Active', c.status === 'Expired' ? 'Credential renewed' : 'Credential reactivated')}>
                                                {c.status === 'Expired' ? 'Renew' : 'Reactivate'}
                                            </button>
                                        )}
                                    </div>
                                </motion.li>
                            ))}
                        </AnimatePresence>
                    </ul>
                )}
                {!query && shown.length > PAGE && (
                    <button type="button" className="btn btn-outline-light btn-sm acc-more" onClick={() => setAll(v => !v)} aria-expanded={all}>
                        {all ? 'Show fewer' : `Show all ${shown.length} credentials`}
                    </button>
                )}

                <h4 className="acc-subhead">Visitor codes <span className="acc-faint">{codes.length} active today</span></h4>
                {guestCodes.length === 0 ? <Empty>No visitor codes.</Empty> : (
                    <ul className="rows">
                        {guestCodes.slice(0, 6).map(code => {
                            const status = codeStatus(code);
                            return (
                                <li className="row" key={code.id}>
                                    <div className="row__main">
                                        <span className="row__title">{code.guest} <span className="acc-faint">· guest of {code.unit}</span></span>
                                        <span className="row__meta"><span className="acc-num">{spaced(code.code)}</span> · {code.date} · {code.window} · {code.uses === 'Once' ? 'One entry' : 'All day'}</span>
                                    </div>
                                    <div className="row__end">
                                        <Chip tone={CODE_TONE[status]}>{status}</Chip>
                                        {status === 'Active' && (
                                            <button type="button" className="btn btn-outline-light btn-sm" onClick={() => revoke(code)}>Revoke</button>
                                        )}
                                    </div>
                                </li>
                            );
                        })}
                    </ul>
                )}
            </div>
        </div>
    );
}
